import React, { useState } from "react";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import api from "@/lib/api";

const INPUT = "w-full border-2 border-black px-3 py-2 text-sm mt-1 bg-white";
const FIELDS = [
  ["rotation_share", "Lead share % (blank = even split)", "e.g. 70"],
  ["daily_cap", "Daily lead cap (blank = no cap)", "e.g. 10"],
  ["monthly_cap", "Monthly lead cap (blank = no cap)", "e.g. 200"],
];

export function capsLabel(c, share) {
  if (!c) return null;
  const bits = [share ? `${share}% share` : "Even share"];
  bits.push(c.daily_cap ? `${c.today}/${c.daily_cap} today` : `${c.today} today`);
  bits.push(c.monthly_cap ? `${c.this_month}/${c.monthly_cap} this month` : `${c.this_month} this month`);
  if (c.at_cap) bits.push("AT CAP — skipped");
  return bits.join(" · ");
}

export const BuyerRotationDialog = ({ b, onClose, onSaved }) => {
  const [form, setForm] = useState({
    rotation_share: b.rotation_share ?? "", daily_cap: b.caps?.daily_cap ?? "", monthly_cap: b.caps?.monthly_cap ?? "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : Number(v)]));
      await api.put(`/admin/buyers/${b.id}/rotation`, body);
      toast.success("Rotation saved");
      onSaved?.();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Check the numbers: share 1–100, caps 1 or more");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="buyer-rotation-dialog">
      <div className="brut-card bg-white w-full max-w-lg my-8">
        <div className="px-4 py-3 border-b-2 border-black flex items-center gap-3">
          <div className="font-chivo font-black uppercase tracking-tight flex flex-wrap gap-x-2"><span>Rotation &amp; caps</span><span>·</span><span>{b.firm_name}</span></div>
          <button type="button" className="ml-auto" onClick={onClose} data-testid="buyer-rotation-close"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-4 flex flex-col gap-3">
          <p className="text-[11px] text-zinc-600">
            When several buyers take the same state, the next visitor goes to the buyer furthest behind its share of today's
            leads in that state. Buyers without a % split what's left evenly. Buyers at a cap are skipped until the day or month resets (Eastern time).
          </p>
          {FIELDS.map(([k, label, ph]) => (
            <label key={k} className="block">
              <span className="label-mono">{label}</span>
              <input type="number" min="1" className={INPUT} placeholder={ph} value={form[k]}
                     onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} data-testid={`buyer-rotation-${k}`} />
            </label>
          ))}
          {b.caps && <div className="text-[11px] font-mono" data-testid="buyer-rotation-current">{capsLabel(b.caps, b.rotation_share)}</div>}
        </div>
        <div className="px-4 py-3 border-t-2 border-black flex justify-end">
          <button type="button" onClick={save} disabled={busy} data-testid="buyer-rotation-save"
                  className="border-2 border-black bg-black text-white px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-40">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
